import { IPartyNumberMap } from '../models/TypeHelper'
import { IRegionalPollMap } from '../App'
import { PredictionModel } from './ReduxStore'
import DataController, { resultForKey } from '../controllers/DataController'
import NationalResult from '../models/NationalResult'
import RandomUniformSwingModel from '../controllers/RandomUniformSwingModel'
import Region from '../models/Region'
import RidingResult from '../models/RidingResult'
import UniformSwingModel from '../controllers/UniformSwingModel'

// The Z score for a normal distribution at the 95% confidence level
const Z_SCORE_FOR_95_PC_NORMAL = 1.96
const ASSUMED_SAMPLE_SIZE = 1500

// Returns a margin of error, assuming that p-hat is equal to 0.5.
export const marginOfError = (
  sampleSize = ASSUMED_SAMPLE_SIZE,
  zScore = Z_SCORE_FOR_95_PC_NORMAL
): number => {
  return zScore * Math.sqrt((0.5 * 0.5) / sampleSize)
}

// Returns a margin of error, assuming that p-hat is equal to 0.5.
export const standardError = (
  marginOfError: number,
  zScore = Z_SCORE_FOR_95_PC_NORMAL
): number => {
  return marginOfError / zScore
}

// Returns a Gaussian (normal) distribution **function** with the given
// mean and standard deviation, from http://stackoverflow.com/a/35599181.
// Uses the https://en.wikipedia.org/wiki/Marsaglia_polar_method
function gaussian(mean: number, stdev: number): Function {
  var y2: number
  var useLast = false
  return function(): number {
    var y1
    if (useLast) {
      y1 = y2
      useLast = false
    } else {
      var x1, x2, w
      do {
        x1 = 2.0 * Math.random() - 1.0
        x2 = 2.0 * Math.random() - 1.0
        w = x1 * x1 + x2 * x2
      } while (w >= 1.0)
      w = Math.sqrt((-2.0 * Math.log(w)) / w)
      y1 = x1 * w
      y2 = x2 * w
      useLast = true
    }

    var retval = mean + stdev * y1
    if (retval > 0) return retval
    return -retval
  }
}

/**
 * Given an IPartyNumberMap of party keys and values (presumably vote percents),
 * returns a new IPartyNumberMap of party keys and values where the values
 * remain proportionately the same but add up to 1. None of the values in the
 * original map can be negative.
 * @param polls The IPartyNumberMap to be normalized.
 */
export const normalizePolls = (polls: IPartyNumberMap): IPartyNumberMap => {
  // Get the values; remember that Object.values will return items in a stable
  // order, which we rely on below.
  const pollValues = Object.values(polls)

  // Get the sum of the poll values.
  const sumOfInputValues = pollValues.reduce((acc, item): number => {
    if (item < 0) throw Error('normalizePolls: item < 0')
    return acc + item
  }, 0)

  const newPollValues: IPartyNumberMap = {}

  Object.keys(polls).forEach((key): void => {
    newPollValues[key] = polls[key] / sumOfInputValues
  })

  return newPollValues
}

export const randomizePolls = (
  polls: IPartyNumberMap,
  marginOfError: number
): IPartyNumberMap => {
  const newPollValues: IPartyNumberMap = {}
  const standardDeviationApprox = standardError(marginOfError)

  // TODO: properly constrain the output values, so that e.g. if Libs are at
  // 35% support the sum of the rest of the parties can't be greater than 65%.
  Object.keys(polls).forEach((key): void => {
    const pollValue = polls[key]
    newPollValues[key] = gaussian(pollValue, standardDeviationApprox)()
  })

  return newPollValues
}

export const nationalPollsToRegionalPolls = (
  polls: IPartyNumberMap,
  baseResultKey: string,
  predictionModel: PredictionModel
): IRegionalPollMap => {
  const baseResult = resultForKey(baseResultKey)

  const differences =
    predictionModel === 'deterministic'
      ? UniformSwingModel.differences(
          baseResult.partyResultMap.votePercents(),
          polls
        )
      : RandomUniformSwingModel.differences(
          baseResult.partyResultMap.votePercents(),
          polls,
          0.03
        )
  const regionalMap: IRegionalPollMap = {}

  Region.allRegionsArray().forEach((region: Region): void => {
    const oldPolls = baseResult
      .resultForRegion(region)
      .partyResultMap.votePercents()
    const newPolls: IPartyNumberMap = {}
    Object.keys(oldPolls).forEach((partyKey: string): void => {
      // NOTE: We need to treat the Bloc in Quebec separately. This is because the
      // national swing in support is so small (typically only 2-3% points) as to
      // under-represent the size of the swing in the province. We can do this by
      // interpreting the difference in national support in percentage terms, and
      // then by applying a proportionately large swing to the Bloc only.
      //
      // In fact, by this logic, we may as well use the proportionate support
      // in each region when adjusting based on national polls; this will be
      // more accurate (though it is biased based on previous levels of support).
      // if (partyKey === Party.BLQ.key && region.key === Region.QC.key) {
      // Get proportionate difference relative to national level
      const result = baseResult.partyResultMap.resultForPartyKey(partyKey)
      if (result) {
        const propDifference = differences[partyKey] / result.votePercent
        const newSupport = oldPolls[partyKey] * (1 + propDifference)
        newPolls[partyKey] = newSupport
      }
      // } else {
      //   newPolls[partyKey] = oldPolls[partyKey] + differences[partyKey]
      // }
    })
    regionalMap[region.key] = newPolls
  })

  return regionalMap
}

export const swingFromRegionalPolls = (
  regionalPolls: IRegionalPollMap,
  baseResultKey: string,
  predictionModel: PredictionModel,
  marginOfError?: number
): NationalResult => {
  const swungRidingResults: RidingResult[] = []

  Object.keys(regionalPolls).forEach((regionKey: string): void => {
    const swungRegionalRidings = DataController.swingResults(
      baseResultKey,
      predictionModel,
      regionalPolls[regionKey],
      (ridingResult: RidingResult): boolean =>
        regionKey === Region.fromProvince(ridingResult.riding.province).key,
      regionKey,
      marginOfError
    )

    swungRegionalRidings.forEach((swungRiding: RidingResult): void => {
      swungRidingResults.push(swungRiding)
    })
  })

  const nationalResult = new NationalResult(swungRidingResults)
  return nationalResult
}
